import { useState, useEffect } from "react";
import { Menu, X, LogIn, LogOut, ChevronDown } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import whitehatLogo from "../assets/whitehat-logo.webp";
import ThemeToggle from "./ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, signOut } = useAuth();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const scrollToSection = (id: string) => {
    setIsOpen(false);
    const scrollWithOffset = () => {
      const element = document.getElementById(id);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    };
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scrollWithOffset, 150);
    } else {
      scrollWithOffset();
    }
  };

  const handleNavigation = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    setIsOpen(false);
    await signOut();
    navigate("/");
  };

  // Updated navigation order per requirements
  const pageLinks = [
    { label: "About", path: "/about" },
    { label: "Divisions", path: "/divisions" },
    { label: "Resources & Roadmap", path: "/roadmap" },
    { label: "Wall of Love", path: "/wall-of-love" },
    { label: "Past Events", path: "/past-sessions" },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <button
            onClick={() => scrollToSection("home")}
            className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity flex-shrink-0"
          >
            <img
              src={whitehatLogo}
              alt=""
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
              loading="eager"
              decoding="async"
              width={40}
              height={40}
            />
            <span className="font-bold text-base sm:text-lg">WhiteHat Hub</span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-3 lg:gap-4">
            <button
              onClick={() => scrollToSection("home")}
              className="text-foreground/80 hover:text-foreground transition-colors font-medium text-sm whitespace-nowrap"
            >
              Home
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-1 text-foreground/80 hover:text-foreground transition-colors font-medium text-sm whitespace-nowrap">
                Explore <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-56">
                {pageLinks.map((link) => (
                  <DropdownMenuItem key={link.path} onClick={() => handleNavigation(link.path)}>
                    {link.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="w-px h-6 bg-border mx-1" />

            {isAdmin && (
              <button
                onClick={() => handleNavigation("/admin")}
                className="text-primary hover:text-accent transition-colors font-medium text-sm whitespace-nowrap"
              >
                Admin
              </button>
            )}

            <ThemeToggle />

            {user ? (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-foreground/80 hover:text-foreground transition-colors font-medium text-sm whitespace-nowrap"
              >
                <LogOut className="w-4 h-4" />
                Log out
              </button>
            ) : (
              <button
                onClick={() => handleNavigation("/auth")}
                className="flex items-center gap-2 text-foreground/80 hover:text-foreground transition-colors font-medium text-sm whitespace-nowrap"
              >
                <LogIn className="w-4 h-4" />
                Admin Login
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 md:hidden">
            <ThemeToggle />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center min-w-11 min-h-11 hover:bg-accent/10 rounded-lg transition-colors"
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >

              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation - No "Explore" label, direct links */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-border absolute left-0 right-0 bg-background/95 backdrop-blur-sm shadow-lg">
            <div className="flex flex-col gap-1 px-4 max-h-[70vh] overflow-y-auto">
              <button
                onClick={() => scrollToSection("home")}
                className="text-foreground/80 hover:text-foreground transition-colors font-medium text-left px-4 py-3 hover:bg-accent/10 rounded-lg"
              >
                Home
              </button>

              {pageLinks.map((link) => (
                <button
                  key={link.path}
                  onClick={() => handleNavigation(link.path)}
                  className="text-foreground/80 hover:text-foreground transition-colors font-medium text-left px-4 py-3 hover:bg-accent/10 rounded-lg"
                >
                  {link.label}
                </button>
              ))}

              <div className="my-2 border-t border-border" />

              {isAdmin && (
                <button
                  onClick={() => handleNavigation("/admin")}
                  className="text-primary hover:text-accent transition-colors font-medium text-left px-4 py-3 hover:bg-accent/10 rounded-lg"
                >
                  Admin
                </button>
              )}

              {user ? (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-foreground/80 hover:text-foreground transition-colors font-medium text-left px-4 py-3 hover:bg-accent/10 rounded-lg"
                >
                  <LogOut className="w-4 h-4" />
                  Log out
                </button>
              ) : (
                <button
                  onClick={() => handleNavigation("/auth")}
                  className="flex items-center gap-2 text-foreground/80 hover:text-foreground transition-colors font-medium text-left px-4 py-3 hover:bg-accent/10 rounded-lg"
                >
                  <LogIn className="w-4 h-4" />
                  Admin Login
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
